import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL } from "@/lib/api";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    const res = await register(form);
    setLoading(false);
    if (!res.ok) {
      setError(res.error);
      toast.error(res.error);
      return;
    }
    toast.success("Account created!");
    nav("/portal");
  };

  return (
    <div data-testid="signup-page" className="min-h-[70vh] flex items-center justify-center px-4 py-16">
      <div className="w-full max-w-md bg-white border border-scu-smoke/15 rounded-md p-8">
        <div className="flex items-center gap-3">
          <img src={LOGO_URL} className="h-12" alt="SCU" />
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-scu-purple font-bold">Join SCU</div>
            <div className="font-bebas text-3xl tracking-wide text-scu-obsidian leading-none">Create Account</div>
          </div>
        </div>
        <form onSubmit={submit} className="mt-8 space-y-5">
          <F label="Full Name"><Input data-testid="signup-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></F>
          <F label="Email"><Input data-testid="signup-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></F>
          <F label="Phone (optional)"><Input data-testid="signup-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></F>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest text-scu-smoke font-bold">Password (min 6 chars)</Label>
            <div className="relative">
              <input
                data-testid="signup-password"
                type={showPw ? "text" : "password"}
                required
                minLength={6}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 pr-10 text-base shadow-sm focus:outline-none focus:ring-1 focus:ring-ring md:text-sm"
              />
              <button data-testid="signup-password-toggle" type="button" onClick={() => setShowPw((s) => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 text-scu-smoke hover:text-scu-purple" aria-label="Toggle password visibility">
                {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          {error && <div data-testid="signup-error" className="text-sm text-red-600">{error}</div>}
          <Button data-testid="signup-submit" type="submit" disabled={loading} className="w-full h-11 bg-scu-purple hover:bg-scu-electric text-white font-bold uppercase tracking-widest">
            {loading ? "Creating…" : "Create Parent Account"}
          </Button>
        </form>
        <div className="mt-6 text-sm text-center text-scu-smoke">
          Already have one? <Link to="/login" data-testid="signup-login-link" className="text-scu-purple hover:text-scu-electric font-bold">Sign in</Link>
        </div>
      </div>
    </div>
  );
}

function F({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-widest text-scu-smoke font-bold">{label}</Label>
      {children}
    </div>
  );
}
